<template>
    <div class="c2c-pdf-backdrop">
        <section class="c2c-pdf-viewer" role="dialog" aria-modal="true" :aria-label="document.display_filename" tabindex="-1">
            <header class="flex items-center gap-3 px-5 py-3 border-b bg-gray-100">
                <strong class="truncate flex-1 min-w-0 text-lg">{{ document.display_filename }}</strong>
            </header>
            <div class="pdf-toolbar">
                <div class="pdf-view-controls" role="group" aria-label="Viewing controls">
                    <div class="pdf-zoom-control" role="group" aria-label="Zoom">
                        <button type="button" class="pdf-button pdf-zoom-button" aria-label="Zoom out" @click="zoom(-0.25)" :disabled="!pdf">−</button>
                        <button type="button" class="pdf-button pdf-zoom-button" aria-label="Zoom in" @click="zoom(0.25)" :disabled="!pdf">+</button>
                    </div>
                    <button type="button" class="pdf-button pdf-borderless" @click="fitWidth" :disabled="!pdf">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M3 4v16M21 4v16M7 12h10M10 9l-3 3 3 3M14 9l3 3-3 3" />
                        </svg>
                        Fit to width
                    </button>
                    <a v-if="document.download_route" :href="document.download_route" class="pdf-button pdf-borderless" aria-label="Download original document" title="Download original document" @click="guardDownload"><download-icon size="18" aria-hidden="true" /> Download</a>
                    <button v-if="canEdit" type="button" class="pdf-button pdf-borderless" @click="editing ? preview() : editing = true" :disabled="finishing || loading">
                        <eye-icon v-if="editing" size="18" aria-hidden="true" />
                        <edit-3-icon v-else size="18" aria-hidden="true" />
                        {{ editing ? 'Preview' : 'Fill in form' }}
                    </button>
                </div>
                <div class="pdf-edit-controls" role="group" aria-label="Form controls">
                    <button v-if="editing && error && !autosaveBlocked" type="button" class="pdf-button" @click="save" :disabled="!dirty || saving">Retry save</button>
                    <button ref="done" type="button" @click="close" :disabled="finishing" :aria-busy="closing" class="pdf-button pdf-done-button">
                        Done
                    </button>
                </div>
            </div>
            <p v-if="reason && !canEdit" class="text-sm px-3 py-2 bg-gray-100">{{ reason }}</p>
            <div v-if="error" role="alert" class="px-3 py-2 text-red-700 bg-red-50">{{ error }} <button class="underline" @click="reload" :disabled="saving || loading">{{ document.preview_route ? 'Retry preview' : 'Reload saved document' }}</button></div>
            <div class="pdf-content">
                <div v-if="progressMessage" role="status" aria-live="polite" class="pdf-loading">
                    <div class="pdf-loading-card">
                        <loader-icon size="40" class="animate-spin text-blue-600" style="animation-duration: 1.5s" aria-hidden="true" />
                        <span>{{ progressMessage }}</span>
                    </div>
                </div>
                <nav ref="thumbnails" class="pdf-thumbnails" aria-label="Page thumbnails">
                    <div v-if="pdf" :key="loadId" class="flex flex-col gap-2">
                        <pdf-thumbnail v-for="number in pageCount" :key="number" :number="number" :pdf="pdf" :runtime="runtime" :active="number === currentPage" :scroller="$refs.thumbnails" @select="goToPage" />
                    </div>
                </nav>
                <div ref="scroll" class="pdf-page-scroll" @scroll.passive="updateCurrentPage">
                    <div v-if="pdf" :key="loadId" class="pdfViewer" :inert="finishing ? '' : null">
                        <pdf-page v-for="number in pageCount" :key="number" ref="pages" :number="number" :pdf="pdf" :runtime="runtime" :scale="scale" :editing="editing" :scroller="$refs.scroll" :field-objects="fieldObjects" :size="pageSizes[number]" @measured="rememberPageSize" @changed="markDirty" />
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
import PdfPage from './pdf-page.vue';
import PdfThumbnail from './pdf-thumbnail.vue';
import { DownloadIcon, EyeIcon, Edit3Icon, LoaderIcon } from 'vue-feather-icons';
import { loadPdfRuntime, pdfAssets, hasFillableFields, lockBackgroundScroll } from './pdf-runtime';
import { applyFieldValues, collectFieldValues } from './pdf-form-values.mjs';
import { createFocusTrap } from 'focus-trap';

export default {
    components: { PdfPage, PdfThumbnail, DownloadIcon, EyeIcon, Edit3Icon, LoaderIcon },
    props: { document: { type: Object, required: true } },
    data() {
        return { pdf: null, runtime: null, fieldObjects: null, loadId: 0, pageCount: 0, currentPage: 1, scale: 1,
            loading: false, editing: false, dirty: false, saving: false, canEdit: false,
            revision: null, reason: '', error: '', finishing: false, closing: false, pageSizes: {}, changeSequence: 0, autosaveBlocked: false };
    },
    computed: {
        progressMessage() {
            if (this.finishing && this.saving) return 'Saving changes…';
            if (this.loading) return this.document.preview_route ? 'Preparing preview…' : 'Loading PDF…';
            return '';
        },
    },
    mounted() {
        this.active = true;
        this.unlockBackground = lockBackgroundScroll();
        this.load().catch(() => {});
        this.trap = createFocusTrap(this.$el, { initialFocus: this.$refs.done, fallbackFocus: this.$el.querySelector('[role=dialog]'), returnFocusOnDeactivate: false, escapeDeactivates: false, allowOutsideClick: false });
        this.trap.activate();
        window.addEventListener('beforeunload', this.beforeUnload);
        window.addEventListener('keydown', this.keydown);
    },
    beforeDestroy() {
        this.active = false;
        this.cancelAutosave();
        if (this.trap) this.trap.deactivate();
        if (this.unlockBackground) this.unlockBackground();
        if (this.loadingTask) this.loadingTask.destroy();
        if (this.pdf) this.pdf.destroy();
        window.removeEventListener('beforeunload', this.beforeUnload);
        window.removeEventListener('keydown', this.keydown);
    },
    methods: {
        async preparePreview() {
            for (let attempt = 0; attempt < 90 && this.active; attempt++) {
                const { data } = await axios.get(this.document.preview_route, { params: attempt === 0 ? { retry: 1 } : {}, timeout: 90000 });
                if (data.status === 'ready') return data.content_url;
                if (data.status !== 'pending') throw new Error('The preview could not be prepared. Please retry or download the original.');
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            if (this.active) throw new Error('The preview is taking longer than expected. Please retry shortly or download the original.');
            return null;
        },
        markDirty() {
            this.changeSequence++; this.dirty = true;
            this.scheduleAutosave();
        },
        cancelAutosave() { clearTimeout(this.autosaveTimer); this.autosaveTimer = null; },
        scheduleAutosave() {
            this.cancelAutosave();
            if (!this.active || !this.editing || !this.canEdit || !this.dirty || this.saving || this.finishing || this.loading || this.autosaveBlocked) return;
            this.autosaveTimer = setTimeout(() => { this.autosaveTimer = null; this.save(); }, 1200);
        },
        rememberPageSize({ number, ...size }) { this.$set(this.pageSizes, number, size); },
        captureView() {
            return { scale: this.scale, page: this.currentPage, top: this.$refs.scroll.scrollTop,
                left: this.$refs.scroll.scrollLeft, thumbnailsTop: this.$refs.thumbnails.scrollTop };
        },
        restoreView(view) {
            this.scale = view.scale; this.currentPage = view.page;
            this.$refs.scroll.scrollTop = view.top; this.$refs.scroll.scrollLeft = view.left;
            this.$refs.thumbnails.scrollTop = view.thumbnailsTop;
        },
        async load(expectedRevision = null, { preserveView = false, preview = false } = {}) {
            const view = preserveView && this.pdf ? this.captureView() : null;
            this.cancelAutosave();
            this.loading = true; this.error = '';
            try {
                const runtime = await loadPdfRuntime();
                let url = this.document.show_route;
                let metadata = { can_edit: false, revision: null };
                if (this.document.preview_route) url = await this.preparePreview();
                if (this.document.pdf_metadata_route) {
                    metadata = (await axios.get(this.document.pdf_metadata_route)).data;
                    if (expectedRevision && metadata.revision !== expectedRevision) {
                        throw new Error('Another session updated this PDF after your save. Reload to review the latest version.');
                    }
                    url = metadata.content_url;
                }
                if (!this.active) return;
                this.loadingTask = runtime.getDocument({ url, withCredentials: true, isEvalSupported: false,
                    enableXfa: false, cMapUrl: `${pdfAssets}cmaps/`, cMapPacked: true,
                    standardFontDataUrl: `${pdfAssets}standard_fonts/`, wasmUrl: `${pdfAssets}wasm/`,
                    maxImageSize: 16000000, canvasMaxAreaInBytes: 64000000 });
                this.loadingTask.onPassword = () => { this.loadingTask.destroy(); };
                const next = await this.loadingTask.promise;
                if (!this.active) { await next.destroy(); return; }
                if (next.numPages > 300) { await next.destroy(); throw new Error('This PDF exceeds the 300-page viewer limit.'); }
                const fields = await next.getFieldObjects();
                applyFieldValues(next, fields, metadata.field_values);
                const old = this.pdf;
                this.runtime = runtime;
                // PDF.js objects must not be traversed by Vue 2's observer.
                this.pdf = Object.preventExtensions(next);
                this.fieldObjects = fields ? Object.freeze(fields) : null;
                this.pageCount = next.numPages; this.currentPage = view ? view.page : 1; this.loadId++;
                if (view) this.scale = view.scale;
                this.canEdit = !!metadata.can_edit && hasFillableFields(fields); this.revision = metadata.revision;
                this.editing = !preview && this.canEdit;
                this.reason = metadata.reason || '';
                this.dirty = false; this.autosaveBlocked = false;
                await this.$nextTick();
                if (old) await old.destroy();
                if (view) this.restoreView(view);
                else await this.fitWidth();
            } catch (error) {
                this.error = error.response?.data?.message || error.message || 'The PDF could not be opened.';
                throw error;
            } finally { this.loading = false; }
        },
        async reload() {
            if (!this.canDiscard()) return;
            try { await this.load(null, { preserveView: true }); } catch (_) {}
        },
        save() {
            if (this.pendingSave) return this.pendingSave;
            if (!this.dirty || !this.canEdit || this.autosaveBlocked) return Promise.resolve(false);
            this.pendingSave = this.persistChanges().finally(() => { this.pendingSave = null; });
            return this.pendingSave;
        },
        async persistChanges() {
            this.cancelAutosave();
            const sequence = this.changeSequence;
            this.saving = true; this.error = '';
            let succeeded = false;
            try {
                const form = { field_values: JSON.stringify(collectFieldValues(this.pdf, this.fieldObjects)), base_revision: this.revision };
                const { data } = await axios.post(this.document.pdf_save_route, form);
                this.revision = data.revision;
                this.dirty = this.changeSequence !== sequence;
                succeeded = true;
                this.$emit('saved', data);
            } catch (error) {
                this.autosaveBlocked = [403, 409, 423, 422].includes(error.response?.status);
                this.error = error.response?.data?.errors?.field_values?.[0] || error.response?.data?.errors?.file?.[0] || error.response?.data?.message || 'Automatic save failed. Your entries are still open; please retry.';
            } finally {
                this.saving = false;
                if (succeeded && this.dirty) this.scheduleAutosave();
            }
            return succeeded;
        },
        canDiscard() { return !this.saving && (!this.dirty || window.confirm('Discard unsaved PDF changes?')); },
        async flushChanges() {
            this.cancelAutosave();
            while (this.dirty || this.pendingSave) {
                if (!await this.save()) return false;
            }
            return true;
        },
        async close() {
            if (this.finishing) return;
            this.finishing = true; this.closing = true;
            try { if (await this.flushChanges()) this.$emit('close'); }
            finally { this.finishing = false; this.closing = false; }
        },
        async preview() {
            if (this.finishing) return;
            this.finishing = true;
            try {
                if (await this.flushChanges()) this.editing = false;
            } catch (_) {} finally { this.finishing = false; }
        },
        beforeUnload(event) {
            if (this.dirty || this.saving) { event.preventDefault(); event.returnValue = ''; }
        },
        keydown(event) { if (event.key === 'Escape') { event.preventDefault(); this.close(); } },
        guardDownload(event) {
            if (this.dirty && !window.confirm('Download the last saved PDF? Your unsaved entries are not included.')) event.preventDefault();
        },
        zoom(change) { this.scale = Math.min(3, Math.max(0.25, this.scale + change)); },
        async fitWidth() {
            if (!this.pdf) return;
            const page = await this.pdf.getPage(this.currentPage);
            this.scale = Math.min(3, Math.max(0.25, (this.$refs.scroll.clientWidth - 40) / page.getViewport({ scale: 1 }).width));
        },
        goToPage(number) {
            number = Math.min(this.pageCount, Math.max(1, Math.floor(number || 1)));
            const page = this.$refs.pages?.[number - 1];
            if (page) { this.currentPage = number; this.$refs.scroll.scrollTop = page.$el.getBoundingClientRect().top - this.$refs.scroll.getBoundingClientRect().top + this.$refs.scroll.scrollTop; }
        },
        updateCurrentPage() {
            const top = this.$refs.scroll.getBoundingClientRect().top;
            const page = this.$refs.pages?.find(page => page.$el.getBoundingClientRect().bottom > top + 30);
            if (page) this.currentPage = page.number;
        },
    },
};
</script>
<style>
.c2c-pdf-backdrop { position: fixed; inset: 0; z-index: 10000; background: #0004; overscroll-behavior: none; }
.c2c-pdf-viewer { position: absolute; inset: clamp(16px, 4vw, 48px); display: flex; flex-direction: column; background: white; border: 1px solid #9ca3af; border-radius: 8px; overflow: hidden; box-shadow: 0 8px 40px #0006; }
.pdf-toolbar, .pdf-view-controls, .pdf-edit-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.pdf-toolbar { padding: 10px 20px; border-bottom: 1px solid #e5e7eb; gap: 12px; }
.pdf-edit-controls { margin-left: auto; justify-content: flex-end; }
.pdf-content { position: relative; display: flex; flex: 1; min-height: 0; }
.pdf-thumbnails { width: 176px; flex: none; overflow-y: auto; overscroll-behavior: contain; padding: 8px 16px; border-right: 1px solid #d1d5db; background: #f3f4f6; }
.pdf-page-scroll { position: relative; isolation: isolate; z-index: 0; flex: 1; min-width: 0; overflow: auto; overscroll-behavior: contain; background: #e5e7eb; overflow-anchor: none; }
.pdf-loading { position: absolute; inset: 0; z-index: 2; display: flex; align-items: center; justify-content: center; background: #f3f4f6cc; }
.pdf-loading-card { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 28px 36px; border-radius: 12px; background: white; box-shadow: 0 4px 20px #0002; font-size: 16px; font-weight: 600; color: #374151; }
.c2c-pdf-viewer .pdf-button { display: inline-flex; align-items: center; justify-content: center; padding: 5px 10px; border: 1px solid #9ca3af; border-radius: 4px; font-size: 13px; }
.pdf-zoom-control { display: inline-flex; flex: none; }
.c2c-pdf-viewer .pdf-zoom-button { width: 32px; height: 28px; padding: 0; font-size: 18px; line-height: 1; }
.c2c-pdf-viewer .pdf-zoom-button:first-child { border-radius: 999px 0 0 999px; }
.c2c-pdf-viewer .pdf-zoom-button:last-child { border-radius: 0 999px 999px 0; border-left: none; }
.c2c-pdf-viewer .pdf-borderless { border: none; gap: 6px; }
.c2c-pdf-viewer button:not(:disabled):hover, .c2c-pdf-viewer a.pdf-button:hover { color: #2563eb; }
.c2c-pdf-viewer .pdf-button:disabled { opacity: .45; cursor: default; }
.c2c-pdf-viewer .pdf-done-button { gap: 6px; background: #2563eb; border-color: #2563eb; color: white; }
.c2c-pdf-viewer button.pdf-done-button:not(:disabled):hover { background: #1d4ed8; border-color: #1d4ed8; color: white; }
@media (max-width: 1200px) { .c2c-pdf-viewer { inset: 16px; } }
@media (max-width: 640px) {
    .c2c-pdf-viewer { inset: 8px; }
    .pdf-thumbnails { width: 112px; padding: 4px 8px; }
}
</style>
